import { useEffect, useRef, useState } from "react";
import { Sunrise, Sun, Sunset, Moon, RefreshCw, Sparkles, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ICONS = { sunrise: Sunrise, sun: Sun, sunset: Sunset, moon: Moon };

// Bucket a Date into one of morning / afternoon / evening / late (Africa/Lagos).
// Kept identical to the backend period_of() so the client can pre-invalidate
// its own cache when the period rolls over without an extra network round-trip.
function periodKeyFromDate(now = new Date()) {
  const utcH = now.getUTCHours();
  const utcM = now.getUTCMinutes();
  const totalMinLagos = (utcH * 60 + utcM + 60) % 1440; // +60 = Africa/Lagos offset
  const h = Math.floor(totalMinLagos / 60);
  if (h >= 5 && h <= 11) return "morning";
  if (h >= 12 && h <= 16) return "afternoon";
  if (h >= 17 && h <= 20) return "evening";
  return "late";
}

/**
 * Period-aware daily briefing card.
 * - Dynamic title/icon that flips as morning → afternoon → evening → late
 * - Auto-refreshes when the browser tab regains focus AND when the period rolls
 * - Ticks every 60s to catch boundary crossings (5am, 12pm, 5pm, 9pm)
 */
export default function MorningBrief({ onAskAi }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const currentPeriod = useRef(periodKeyFromDate());

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const r = await api.get("/coach/morning-brief");
      setData(r.data);
      currentPeriod.current = r.data.period.key;
    } catch (err) {
      if (!silent) toast.error("Could not load today's brief");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load + refresh on foreground/period-boundary + live updates.
  useEffect(() => {
    load();
    const onVisible = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    // Iter33 live sync: refetch when any transaction/inventory/chat/brain_sync
    // event arrives from another channel (e.g. a WhatsApp write).
    const onLive = (e) => {
      const kind = e?.detail?.kind;
      if (["transaction", "inventory", "brain_sync"].includes(kind)) {
        load({ silent: true });
      }
    };
    window.addEventListener("kobolog:live", onLive);
    // Every 60s: if the period key changed under our feet, refresh.
    const tick = setInterval(() => {
      const now = periodKeyFromDate();
      if (now !== currentPeriod.current) load({ silent: true });
    }, 60000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("kobolog:live", onLive);
      clearInterval(tick);
    };
  }, []);

  if (loading) {
    return (
      <div className="rounded-3xl bg-white border border-border p-5 shadow-sm animate-pulse">
        <div className="h-4 w-32 bg-kudi-cream rounded mb-3" />
        <div className="h-3 w-48 bg-kudi-cream rounded mb-2" />
        <div className="h-3 w-full bg-kudi-cream rounded" />
      </div>
    );
  }
  if (!data) return null;

  const Icon = ICONS[data.period.icon] || Sunrise;
  const iconBg = {
    sunrise: "bg-amber-50 text-amber-500",
    sun: "bg-yellow-50 text-yellow-500",
    sunset: "bg-orange-50 text-orange-500",
    moon: "bg-indigo-50 text-indigo-500",
  }[data.period.icon] || "bg-amber-50 text-amber-500";

  return (
    <div className="rounded-3xl bg-white border border-border p-5 shadow-sm" data-testid="morning-brief-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${iconBg}`}>
            <Icon className="w-4.5 h-4.5" strokeWidth={2.2} />
          </div>
          <h2 className="font-heading text-xl font-bold" data-testid="brief-title">{data.period.title}</h2>
        </div>
        <button
          onClick={() => load()}
          disabled={refreshing}
          aria-label="Refresh brief"
          className="w-9 h-9 rounded-full bg-kudi-cream hover:bg-kudi-cream/70 disabled:opacity-40 flex items-center justify-center text-kudi-ink/60 transition"
          data-testid="brief-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      <p className="text-[15px] mb-3 font-medium" data-testid="brief-greeting">{data.greeting}</p>

      <div className="text-[15px] leading-relaxed text-kudi-ink/85 whitespace-pre-line" data-testid="brief-body">{data.body}</div>

      {data.focus && (
        <div className="text-[15px] leading-relaxed text-kudi-ink/85 mt-3 whitespace-pre-line" data-testid="brief-focus">
          {renderInlineBold(data.focus)}
        </div>
      )}

      {data.weekly_signal && (
        <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-xs font-medium" data-testid="brief-weekly">
          <TrendingUp className="w-3.5 h-3.5" />
          {stripLeadingEmoji(data.weekly_signal)}
        </div>
      )}

      <button
        onClick={onAskAi}
        className="mt-4 w-full bg-kudi-green hover:bg-kudi-green-dark text-white font-semibold rounded-2xl py-3 flex items-center justify-center gap-2 transition"
        data-testid="brief-ask-ai"
      >
        <Sparkles className="w-4 h-4" /> Ask the AI
      </button>
    </div>
  );
}

// Convert **bold** markdown (as used by the backend) into real <strong> spans.
function renderInlineBold(str) {
  const parts = String(str).split(/\*\*(.+?)\*\*|\*(.+?)\*/g);
  return parts.map((p, i) => {
    if (!p) return null;
    // Odd-indexed segments are the captured bold groups
    if (i % 3 === 1 || i % 3 === 2) return <strong key={i}>{p}</strong>;
    return <span key={i}>{p}</span>;
  });
}

function stripLeadingEmoji(str) {
  // Keep the emoji visible in the badge already via the icon; just show the text after.
  return String(str).replace(/^[^\w]+/, "").trim() || str;
}
